// packages/components/src/agent_profile/showcase/IndustriesSelector.tsx
import React, { useCallback } from 'react';
import { Tooltip, Card, Button } from '@dopomogai/ui';

export type IndustryOption = {
  key: string;
  title?: string;
  description?: string;
  default_tier?: 'trainee' | 'junior' | 'middle' | 'senior';
};

type Props = {
  industries: IndustryOption[];
  value?: string; // selected key
  onChange: (key?: string) => void; // pass undefined to clear
  className?: string;
  showClear?: boolean;
  label?: string;
  helperText?: string;
};

export function IndustriesSelector({
  industries,
  value,
  onChange,
  className,
  showClear = true,
  label = 'Industries',
  helperText = 'This agent is adjusted to various industries — select yours to tailor the plan.',
}: Props) {
  if (!industries || industries.length === 0) return null;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') onChange(undefined);
      if (!['ArrowRight', 'ArrowLeft'].includes(e.key)) return;

      const keys = industries.map((x) => x.key);
      const idx = value ? keys.indexOf(value) : -1;
      const next =
        e.key === 'ArrowRight'
          ? keys[Math.min(keys.length - 1, Math.max(0, idx + 1))]
          : keys[Math.max(0, idx - 1)];
      if (next) onChange(next);
    },
    [industries, onChange, value]
  );

  return (
    <Card className={className}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-900 border-2 border-black rounded-full px-3 py-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-700" />
              <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
            </div>
            <p className="text-xs text-slate-600 mt-2">{helperText}</p>
          </div>
          {showClear && value && (
            <Button
              variant="ghost"
              size="sm"
              text="Clear"
              aria-label="Clear industry selection"
              onClick={() => onChange(undefined)}
            />
          )}
        </div>

        {/* Segmented-like control; responsive and compact */}
        <div
          role="tablist"
          aria-label="Select industry"
          onKeyDown={handleKeyDown}
          className="mt-3 border-2 border-black rounded-xl bg-white p-1 grid grid-cols-2 sm:grid-cols-4 gap-1"
        >
          {industries.map((opt) => {
            const active = value === opt.key;
            const btn = (
              <button
                key={opt.key}
                role="tab"
                aria-selected={active}
                aria-controls={`industry-panel-${opt.key}`}
                onClick={() => onChange(opt.key)}
                className={[
                  'border-2 border-black rounded-lg',
                  'flex items-center justify-center gap-1.5',
                  'px-3 py-2 text-sm font-extrabold',
                  'transition-all duration-150 transform',
                  'hover:-translate-x-px hover:-translate-y-px hover:shadow-[2px_2px_0_0_rgb(17,24,39)]',
                  active ? 'bg-amber-200 text-amber-900' : 'bg-slate-50 text-slate-900',
                ].join(' ')}
              >
                {opt.title || opt.key}
              </button>
            );
            return opt.description ? (
              <Tooltip key={opt.key} text={opt.description}>
                {btn}
              </Tooltip>
            ) : (
              btn
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export default IndustriesSelector;
